/**
 * Platform Users Hook
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  platformUserService,
  CreatePlatformUserDto,
  UpdatePlatformUserDto,
} from '../services/platformUserService';

const QUERY_KEYS = {
  users: ['platform', 'users'],
  user: (id: string) => ['platform', 'users', id],
  roles: ['platform', 'roles'],
};

export function usePlatformUsers() {
  return useQuery({
    queryKey: QUERY_KEYS.users,
    queryFn: platformUserService.getAll,
  });
}

export function usePlatformUser(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.user(id),
    queryFn: () => platformUserService.getById(id),
    enabled: !!id,
  });
}

export function usePlatformRoles() {
  return useQuery({
    queryKey: QUERY_KEYS.roles,
    queryFn: platformUserService.getRoles,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useCreatePlatformUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreatePlatformUserDto) => platformUserService.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users });
    },
  });
}

export function useUpdatePlatformUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdatePlatformUserDto }) =>
      platformUserService.update(id, dto),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.user(id) });
    },
  });
}

export function useDeletePlatformUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => platformUserService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users });
    },
  });
}
